import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { checkPazaramaStatus } from '../../../api/Pazarama';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { IntegrationStatusBadge } from '../../../components/IntegrationStatus/IntegrationStatusBadge';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';
import { PartnerPlatformSettingsProps } from '../../../interfaces/Settings/PartnerPlatformSettingsProps';

export const PazaramaSettings = ({ onSave }: PartnerPlatformSettingsProps) => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [loadingStatus, setLoadingStatus] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.PAZARAMA)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => {
        refreshStatus();
        setLoading(false);
      });
  };

  const refreshStatus = () => {
    setLoadingStatus(true);
    checkPazaramaStatus()
      .then(() => setErrorMessage(undefined))
      .catch((err) => {
        setErrorMessage(err.message ?? 'Bilinmeyen hata');
      })
      .finally(() => setLoadingStatus(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(SettingsType.PAZARAMA, key, settings, newValue),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings)
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
        onSave?.();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '95%',
      }}
    >
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            ...sharedStyles.h5,
            fontWeight: 'bold',
          }}
        >
          Pazarama
        </Typography>

        <IntegrationStatusBadge
          status={
            loadingStatus ? 'loading' : errorMessage ? 'error' : 'success'
          }
          errorMessage={errorMessage}
        />
      </Box>
      <Divider />

      <SettingsRow
        name='Stok ve Fiyat Eşitleme'
        valueType={SettingsValueType.BOOLEAN}
        value={getSettingsValue(settings, 'pazarama_status')}
        onChange={(newValue) => handleChange('pazarama_status', newValue)}
      />
      <SettingsRow
        name='Komisyon Oranı (%)'
        valueType={SettingsValueType.NUMBER}
        value={getSettingsValue(settings, 'pazarama_commission')}
        onChange={(newValue) => handleChange('pazarama_commission', newValue)}
        minValue={0}
        maxValue={100}
      />
      <SettingsRow
        name='API Key'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'pazarama_client_id')}
        onChange={(newValue) => handleChange('pazarama_client_id', newValue)}
        masked
      />
      <SettingsRow
        name='API Secret'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'pazarama_client_secret')}
        onChange={(newValue) =>
          handleChange('pazarama_client_secret', newValue)
        }
        masked
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />
      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
